import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext, useState } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

interface ResyncResponse {
	organizationId: string;
	workflows: string[];
	claimedPtIds: string[];
	claimedCccIds: string[];
}

interface PullStatusResponse {
	organizationId: string;
	target: string;
	status: 'pending' | 'in-progress' | 'completed' | 'failed' | null;
	locations: Array<{ id: string; name: string; address?: string; city?: string; state?: string; zip?: string }> | null;
	suppliers?: unknown[] | null;
	/** True once the worker persisted the location list, even if the supplier crawl is still running. */
	locationsReady?: boolean;
	error: string | null;
}

export const useResyncPlatformLocations = () => {
	const sessionContext = useContext(SessionContext);
	const [resyncData, setResyncData] = useState<ResyncResponse | null>(null);

	const resync = useMutation({
		mutationFn: async (input?: { targets?: Array<'partstrader' | 'ccc'> }) => {
			const { data } = await axios.post<ResyncResponse>(
				`${API_URL}/location/resync-platforms`,
				input?.targets ? { targets: input.targets } : {},
				{ headers: { Authorization: sessionContext?.sessionId ?? '' } },
			);
			setResyncData(data);
			return data;
		},
	});

	const ptStatus = useQuery({
		queryKey: ['resync-pt-status', resyncData?.organizationId],
		queryFn: async () => {
			const { data } = await axios.get<PullStatusResponse>(
				`${API_URL}/onboarding/pull-status`,
				{
					params: { target: 'partstrader' },
					headers: { Authorization: sessionContext?.sessionId ?? '' },
				},
			);
			return data;
		},
		enabled: !!resyncData && resyncData.workflows.includes('partstrader'),
		refetchInterval: (query) => {
			const d = query.state.data;
			if (d?.status === 'completed' || d?.status === 'failed') return false;
			return 3000;
		},
	});

	const cccStatus = useQuery({
		queryKey: ['resync-ccc-status', resyncData?.organizationId],
		queryFn: async () => {
			const { data } = await axios.get<PullStatusResponse>(
				`${API_URL}/onboarding/pull-status`,
				{
					params: { target: 'ccc' },
					headers: { Authorization: sessionContext?.sessionId ?? '' },
				},
			);
			return data;
		},
		enabled: !!resyncData && resyncData.workflows.includes('ccc'),
		refetchInterval: (query) => {
			const d = query.state.data;
			if (d?.status === 'completed' || d?.status === 'failed') return false;
			return 3000;
		},
	});

	const ptDone = !resyncData?.workflows.includes('partstrader') || ptStatus.data?.status === 'completed' || ptStatus.data?.status === 'failed';
	const cccDone = !resyncData?.workflows.includes('ccc') || cccStatus.data?.status === 'completed' || cccStatus.data?.status === 'failed';
	const allDone = !!resyncData && ptDone && cccDone;
	const hasFailed = ptStatus.data?.status === 'failed' || cccStatus.data?.status === 'failed';

	// "Locations ready" means the worker has persisted the location list (intermediate write
	// during the PT pull) — even though the supplier crawl is still finishing in the background.
	// Lets the modal advance to the mapping step without waiting for suppliers.
	// `locationsReady` is set by the API once `locationsCompletedAt` lands; for CCC there's no
	// supplier crawl phase, so completion implies locations-ready.
	const ptLocationsReady = !resyncData?.workflows.includes('partstrader')
		|| ptStatus.data?.locationsReady === true
		|| ptStatus.data?.status === 'completed'
		|| ptStatus.data?.status === 'failed';
	const cccLocationsReady = !resyncData?.workflows.includes('ccc')
		|| cccStatus.data?.locationsReady === true
		|| cccStatus.data?.status === 'completed'
		|| cccStatus.data?.status === 'failed';
	const locationsReady = !!resyncData && ptLocationsReady && cccLocationsReady;

	const reset = () => setResyncData(null);

	return {
		resync,
		ptStatus,
		cccStatus,
		resyncData,
		allDone,
		locationsReady,
		hasFailed,
		isPolling: !!resyncData && !allDone,
		ptLocations: ptStatus.data?.locations ?? null,
		cccLocations: cccStatus.data?.locations ?? null,
		claimedPtIds: resyncData?.claimedPtIds ?? [],
		claimedCccIds: resyncData?.claimedCccIds ?? [],
		ptError: ptStatus.data?.error ?? null,
		cccError: cccStatus.data?.error ?? null,
		reset,
	};
};
